// VerReservas.tsx — Co Marca — Reservas del Espacio POS
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, NavigateFunction } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    Calendar, Clock, CreditCard, X,
    ArrowLeft, MoreVertical, Banknote,
    AlertCircle, CheckCircle2, XCircle,
    User, Search, Timer, Zap, RefreshCw
} from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import {
    fetchCitas, fetchClientes, Cliente, Cita,
    fetchPrecioPlanesServicios, PrecioPlanServicio,
    fetchPlanDuracionConfigs, PlanDuracionConfig,
    finalizarTemporizador
} from '../../../services/citasService';
import { fetchPagosByCitaId, Pago } from '../../../services/pagosService';
import { usePOS } from '../../../context/POSContext';
import PagosModal from '../pagos/PagosModal';
import FacturaReserva from '../../../components/posCita/FacturaReserva';

// ─────────────────────────────────────────────
// Design system
// ─────────────────────────────────────────────
// ── Paleta Pasantías ─────────────────────────────────────────
const C = {
    brand:        '#61257b',   // púrpura primario
    brandDark:    '#3d1250',   // púrpura oscuro
    brandLight:   '#8b3fa8',   // púrpura suave
    accent1:      '#c1348b',   // magenta / acento principal
    accent1Dark:  '#8f1f64',   // magenta oscuro
    accent2:      '#ea8406',   // naranja cálido
    accent2Dark:  '#b86300',   // naranja oscuro
    accent3:      '#ffd97c',   // amarillo dorado
    accent3Dark:  '#f0b429',   // dorado intenso
    primary:      '#c1348b',
    primaryDark:  '#8f1f64',
    secondary:    '#61257b',
    secondaryDark:'#3d1250',
    secondaryLight:'#8b3fa8',
    accentDark:   '#b86300',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
.vr-page * { font-family:'Inter',sans-serif !important; box-sizing:border-box; }
@keyframes vrSpin    { to{transform:rotate(360deg);} }
@keyframes vrFadeUp  { from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);} }
@keyframes vrPulse   { 0%,100%{opacity:1;}50%{opacity:.55;} }
@keyframes vrSlideIn { from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);} }

.vr-card {
    background:#fff;border-radius:18px;
    border:2px solid #f1f5f9;overflow:hidden;
    transition:all .3s cubic-bezier(.16,1,.3,1);
    display:flex;flex-direction:column;
    animation:vrFadeUp .4s cubic-bezier(.16,1,.3,1) both;
}
.vr-card:hover { border-color:#e2e8f0; box-shadow:0 12px 40px rgba(0,0,0,.08); transform:translateY(-3px); }

.vr-badge {
    display:inline-flex;align-items:center;gap:4px;
    padding:2px 8px;border-radius:6px;font-size:.68rem;font-weight:700;
    white-space:nowrap;
}

.vr-btn-primary {
    display:inline-flex;align-items:center;justify-content:center;gap:6px;
    padding:9px 16px;border-radius:11px;font-size:.82rem;font-weight:800;
    border:none;cursor:pointer;transition:all .22s cubic-bezier(.16,1,.3,1);
}
.vr-btn-primary:disabled { opacity:.6; cursor:wait; }
.vr-btn-primary.blue {
    background:linear-gradient(135deg,${C.secondary},${C.secondaryDark});
    color:#fff;box-shadow:0 3px 12px ${C.secondary}35;
}
.vr-btn-primary.blue:hover { transform:translateY(-1px);box-shadow:0 6px 18px ${C.secondary}45; }
.vr-btn-primary.red {
    background:linear-gradient(135deg,${C.primary},${C.primaryDark});
    color:#fff;box-shadow:0 3px 12px ${C.primary}30;
}
.vr-btn-primary.red:hover { transform:translateY(-1px);box-shadow:0 6px 18px ${C.primary}40; }

.vr-dropdown { animation:vrSlideIn .2s cubic-bezier(.16,1,.3,1); }
.vr-modal-overlay { animation:vrFadeUp .2s ease; }
.vr-modal-dialog  { animation:vrSlideIn .3s cubic-bezier(.16,1,.3,1); }

.vr-scroll::-webkit-scrollbar{width:5px;height:5px;}
.vr-scroll::-webkit-scrollbar-track{background:transparent;}
.vr-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.25);border-radius:10px;}
.vr-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.45);}

.vr-table-row:hover{background:#f8fafc;}
`;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const fmtDate = (s?: string|null) => {
    if (!s) return 'No programada';
    const p = s.split('-');
    if (p.length===3) { const d=new Date(+p[0],+p[1]-1,+p[2]); if (!isNaN(d.getTime())) return d.toLocaleDateString('es-ES',{year:'numeric',month:'short',day:'numeric'}); }
    try { const d=new Date(s); return isNaN(d.getTime())?'Fecha inválida':d.toLocaleDateString('es-ES',{year:'numeric',month:'short',day:'numeric'}); } catch { return 'Fecha inválida'; }
};
const fmtTime = (t?: string|null) => { if (!t) return 'Sin hora'; try { return t.substring(0,5); } catch { return 'Hora inválida'; } };
const fmtCurrency = (n: number) => new Intl.NumberFormat('es-BO',{style:'currency',currency:'BOB'}).format(n);
const fmtMonto = (m: any) => { if (m==null) return '0.00'; const n=Number(m); return isNaN(n)?'0.00':n.toFixed(2); };

// Badges
type BS = {background:string;color:string;border:string};
const estadoBadge = (e: string): BS => {
    switch(e.toLowerCase()){
        case 'confirmada':case 'completada': return {background:`${C.secondaryLight}14`,color:'#065f46',border:`1px solid ${C.secondaryLight}30`};
        case 'pendiente':  return {background:`${C.accentDark}12`,color:C.accentDark,border:`1px solid ${C.accentDark}25`};
        case 'cancelada':  return {background:`${C.primary}10`,color:C.primaryDark,border:`1px solid ${C.primary}22`};
        default:           return {background:'#f1f5f9',color:'#64748b',border:'1px solid #e2e8f0'};
    }
};
const estadoPagoBadge = (e?: string): BS => {
    switch((e||'').toLowerCase()){
        case 'pagado':case 'completado':case 'pagado completo': return {background:`${C.secondaryLight}14`,color:'#065f46',border:`1px solid ${C.secondaryLight}30`};
        case 'pendiente': return {background:`${C.accentDark}12`,color:C.accentDark,border:`1px solid ${C.accentDark}25`};
        case 'parcial':   return {background:`${C.secondary}10`,color:C.secondary,border:`1px solid ${C.secondary}22`};
        case 'cancelado': return {background:`${C.primary}10`,color:C.primaryDark,border:`1px solid ${C.primary}22`};
        default:          return {background:'#f1f5f9',color:'#64748b',border:'1px solid #e2e8f0'};
    }
};
const ejecucionBadge = (e: string): BS => {
    switch(e){ case 'PorComenzar': return {background:`${C.accentDark}12`,color:C.accentDark,border:`1px solid ${C.accentDark}25`}; case 'EnCurso': return {background:`${C.secondary}10`,color:C.secondary,border:`1px solid ${C.secondary}22`}; default: return {background:'#f1f5f9',color:'#64748b',border:'1px solid #e2e8f0'}; }
};
const ejecucionLabel = (e: string) => ({PorComenzar:'Por Comenzar',EnCurso:'En Curso'}[e]||e);
const ejecucionIcon = (e: string) => e==='EnCurso'?<Clock size={11}/>:<AlertCircle size={11}/>;
const estadoIcon = (e: string) => { switch(e.toLowerCase()){ case 'confirmada':case 'completada': return <CheckCircle2 size={11}/>; case 'cancelada': return <XCircle size={11}/>; default: return <AlertCircle size={11}/>; } };

// ─────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────
interface CitaConCliente extends Cita { clienteInfo?: Cliente; }

const LOAD_ERROR = 'No se pudieron cargar las reservas del espacio. Intenta nuevamente.';

interface ScopedProps {
    sucursalId: number | null;
    sesionPosId: number | null;
    /** Grant del operador identificado por PIN sobre ``sesionPosId``. */
    operadorToken: string | null;
    espacioId: string | undefined;
    navigate: NavigateFunction;
}

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
// La identidad de la vista es sucursal + sesión POS + espacio. Cualquier cambio
// en una dimensión desmonta el árbol anterior y monta uno nuevo (`key`): desde
// el primer render de la nueva identidad no existe ninguna cita, plan,
// configuración, pago ni selección de la anterior, y toda continuación
// asíncrona de la instancia vieja queda invalidada (mountedRef/generaciones).
const VerReservas: React.FC = () => {
    const { espacioId } = useParams<{ espacioId: string }>();
    const navigate = useNavigate();
    const { sucursalId, sesionActiva, operadorActivo } = usePOS();
    const sesionPosId = sesionActiva?.id ?? null;
    // El grant del operador (PIN) NO forma parte de la identidad: cambiar de
    // operador no desmonta la vista, solo habilita/deshabilita cobrar y finalizar.
    const operadorToken = operadorActivo?.token ?? null;
    const identity = `${sucursalId ?? ''}|${sesionPosId ?? ''}|${espacioId ?? ''}`;
    return (
        <VerReservasScoped
            key={identity}
            sucursalId={sucursalId}
            sesionPosId={sesionPosId}
            operadorToken={operadorToken}
            espacioId={espacioId}
            navigate={navigate}
        />
    );
};

const VerReservasScoped: React.FC<ScopedProps> = ({ sucursalId, sesionPosId, operadorToken, espacioId, navigate }) => {
    const canLoad = !!sucursalId && !!espacioId;
    const [citas, setCitas]                     = useState<CitaConCliente[]>([]);
    const [planesPrecio, setPlanesPrecio]       = useState<PrecioPlanServicio[]>([]);
    const [configsDuracion, setConfigsDuracion] = useState<PlanDuracionConfig[]>([]);
    const [loading, setLoading]                 = useState(canLoad);
    const [error, setError]                     = useState<string|null>(null);
    const [reloadKey, setReloadKey]             = useState(0);
    const [selectedCita, setSelectedCita]       = useState<Cita|null>(null);
    const [pagosCita, setPagosCita]             = useState<Pago[]>([]);
    const [isPagosModalOpen, setPagosModalOpen] = useState(false);
    const [selectedCitaForPayment, setSelectedCitaForPayment] = useState<Cita|null>(null);
    const [openDropdownId, setOpenDropdownId]   = useState<number|null>(null);
    const [finalizingId, setFinalizingId]       = useState<number|null>(null);
    const [now, setNow]                         = useState(new Date());

    // Guards de continuación: la instancia vive para UNA identidad. Las
    // lecturas usan su propia generación (reintentos); las mutaciones capturan
    // la época de identidad. Ambas se invalidan al desmontar.
    const mountedRef = useRef(true);
    const loadGenerationRef = useRef(0);
    const mutationEpochRef = useRef(0);
    const isCurrentLoad = useCallback((generation: number) => mountedRef.current && generation === loadGenerationRef.current, []);
    const beginMutation = useCallback(() => {
        const epoch = mutationEpochRef.current;
        return () => mountedRef.current && epoch === mutationEpochRef.current;
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            loadGenerationRef.current += 1;
            mutationEpochRef.current += 1;
        };
    }, []);

    useEffect(()=>{ const iv=setInterval(()=>setNow(new Date()),1000); return()=>clearInterval(iv); },[]);

    useEffect(()=>{
        const generation = ++loadGenerationRef.current;
        // Vaciar SIEMPRE antes de cargar: ninguna respuesta previa sobrevive.
        setCitas([]); setPlanesPrecio([]); setConfigsDuracion([]);
        setSelectedCita(null); setPagosCita([]); setPagosModalOpen(false); setSelectedCitaForPayment(null);
        setOpenDropdownId(null); setFinalizingId(null);
        setError(null);
        if(!canLoad){ setLoading(false); return; }
        setLoading(true);
        Promise.all([fetchCitas(sucursalId as number),fetchClientes(),fetchPrecioPlanesServicios(),fetchPlanDuracionConfigs()])
            .then(([citasData,clientesData,planesData,configsData])=>{
                if(!isCurrentLoad(generation)) return;
                setPlanesPrecio(planesData||[]);
                setConfigsDuracion((configsData||[]).filter(c=>c.estado));
                const filtradas=(citasData||[]).filter(c=>c.espacio_servicio===Number(espacioId)&&(c.estado_ejecucion==='PorComenzar'||c.estado_ejecucion==='EnCurso'||(c.estado_ejecucion==='Finalizada'&&c.estado_pago!=='Pagado Completo')));
                setCitas(filtradas.map(c=>({...c,clienteInfo:(clientesData||[]).find(cl=>cl.usuario?.id===c.cliente)})).sort((a,b)=>b.id-a.id));
                setError(null);
            })
            .catch((err: unknown)=>{
                if(!isCurrentLoad(generation)) return;
                /* Browser diagnostics omitted. */
                setCitas([]); setPlanesPrecio([]); setConfigsDuracion([]);
                setError(LOAD_ERROR);
            })
            .finally(()=>{ if(isCurrentLoad(generation)) setLoading(false); });
    },[sucursalId,espacioId,canLoad,reloadKey,isCurrentLoad]);

    // Cerrar dropdown al click fuera
    useEffect(()=>{
        const fn=(e:MouseEvent)=>{ if(!(e.target as HTMLElement).closest('[data-dd]')) setOpenDropdownId(null); };
        document.addEventListener('click',fn); return()=>document.removeEventListener('click',fn);
    },[]);

    const citaUsaTimer=(cita:CitaConCliente)=>{
        const plan=planesPrecio.find(p=>p.id===cita.precio_plan_servicio);
        return plan?configsDuracion.find(c=>c.id===plan.plan_duracion_config)?.usa_temporizador||false:false;
    };

    const calcularTiempoYCosto=(cita:CitaConCliente)=>{
        const plan=planesPrecio.find(p=>p.id===cita.precio_plan_servicio);
        const config=plan?configsDuracion.find(c=>c.id===plan.plan_duracion_config):undefined;
        if(!config||!config.usa_temporizador)return null;
        const inicio=new Date(`${cita.fecha_inicio}T${cita.hora_inicio}`);
        const fin=cita.hora_fin?new Date(`${cita.fecha_fin}T${cita.hora_fin}`):null;
        const base=fin&&fin<now?fin:now;
        const mins=differenceInMinutes(base,inicio);
        let horas=0;
        if(mins>0){const he=Math.floor(mins/60),me=mins%60;horas=me>15?he+1:Math.max(1,he);}
        let costo=0;
        if(plan){const pi=Number(plan.precio_inicial)||0,pp=Number(plan.precio_posterior)||0,di=plan.duracion_inicial||0;if(di>0){const hi=Math.min(horas,di),hp=Math.max(horas-di,0);costo=(hi*pi)+(hp*pp);}else costo=horas*pi;}
        return {minutos:mins,horas,costo:Math.round(costo*100)/100,activa:!fin||fin>now};
    };

    const handleVerPagos=async(cita:Cita)=>{
        const isCurrent=beginMutation();
        setOpenDropdownId(null);
        try{
            const pagos=await fetchPagosByCitaId(cita.id);
            if(!isCurrent())return;
            setPagosCita(pagos||[]);setSelectedCita(cita);
        }catch(e){
            if(!isCurrent())return;
            /* Browser diagnostics omitted. */
            alert('No se pudo cargar el historial de pagos de la reserva.');
        }
    };

    const handleFinalizar=async(cita:CitaConCliente)=>{
        // La caja explícita de ESTA identidad y el grant del operador (PIN)
        // son la autoridad: el backend exige caja compartida abierta, habilitada
        // para citas y de la sucursal de la cita, y un operador responsable vigente.
        if(!operadorToken){alert('Identifica al operador de caja con su PIN antes de finalizar.');return;}
        const isCurrent=beginMutation();
        const citaId=cita.id;
        setFinalizingId(citaId);
        try{
            const r=await finalizarTemporizador(citaId,sesionPosId,operadorToken);
            if(!isCurrent())return;
            setCitas(p=>p.map(c=>c.id===citaId?{...c,hora_fin:r.hora_fin,fecha_fin:r.fecha_fin,total:Number(r.total)}:c));
        }catch(err:any){
            if(!isCurrent())return;
            const data=err?.response?.data;const detail=data?.sesion_pos||data?.cita||data?.total||data?.error||data?.detail||err?.message;
            alert(`No se pudo finalizar el temporizador${detail?`: ${Array.isArray(detail)?detail.join(' '):detail}`:''}`);
        }finally{
            if(isCurrent())setFinalizingId(p=>p===citaId?null:p);
        }
    };

    const actualizarCitaDespuesDePago=(id:number,monto:number)=>{
        setCitas(p=>p.map(c=>c.id===id?{...c,monto_pagado:monto}:c));
    };

    const handlePagoSuccess=async()=>{
        if(!selectedCitaForPayment)return;
        const isCurrent=beginMutation();
        const citaId=selectedCitaForPayment.id;
        try{
            const p=await fetchPagosByCitaId(citaId);
            if(!isCurrent())return;
            actualizarCitaDespuesDePago(citaId,(p||[]).reduce((s,x)=>s+Number(x.monto),0));
            setPagosCita(prev=>(selectedCita?.id===citaId?(p||[]):prev));
        }catch(e){
            if(!isCurrent())return;
            /* Browser diagnostics omitted. */
        }finally{
            if(isCurrent()){setPagosModalOpen(false);setSelectedCitaForPayment(null);}
        }
    };

    // ── Modal historial pagos via portal ──
    const ModalHistorial = selectedCita&&!selectedCitaForPayment ? createPortal(
        <div className="vr-page vr-modal-overlay" style={{position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',background:'rgba(15,23,42,.55)',backdropFilter:'blur(6px)'}}>
            <div className="vr-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="vr-historial-title" onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px',width:'100%',maxWidth:'540px',maxHeight:'82vh',display:'flex',flexDirection:'column',boxShadow:'0 32px 80px rgba(0,0,0,.2)',border:'1.5px solid #f1f5f9',overflow:'hidden'}}>
                <div style={{height:'4px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})`,flexShrink:0}}/>
                <div style={{padding:'18px 20px',borderBottom:'1.5px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        <div style={{width:'34px',height:'34px',borderRadius:'10px',background:`${C.secondary}10`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                            <CreditCard size={16} color={C.secondary}/>
                        </div>
                        <div>
                            <p id="vr-historial-title" style={{fontSize:'.92rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Pagos de la reserva</p>
                            <p style={{fontSize:'.7rem',color:'#94a3b8'}}>{selectedCita.codigo||`CITA-${selectedCita.id}`}</p>
                        </div>
                    </div>
                    <button aria-label="Cerrar historial de pagos" onClick={()=>setSelectedCita(null)} style={{width:'30px',height:'30px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
                        <X size={14} color="#64748b"/>
                    </button>
                </div>
                <div className="vr-scroll" style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'flex',flexDirection:'column',gap:'8px'}}>
                    {pagosCita.length===0?(
                        <div style={{textAlign:'center',padding:'32px 16px'}}>
                            <Banknote size={32} color="#d1d5db" style={{margin:'0 auto 10px'}}/>
                            <p style={{fontSize:'.85rem',color:'#94a3b8',fontWeight:'500'}}>Sin pagos registrados</p>
                        </div>
                    ):pagosCita.map((p,i)=>(
                        <div key={p.id??i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px',background:'#f8fafc',borderRadius:'12px',border:'1.5px solid #f1f5f9'}}>
                            <div>
                                <p style={{fontSize:'.85rem',fontWeight:'700',color:'#374151'}}>{p.tipo_pago_nombre||'Pago'}</p>
                                <p style={{fontSize:'.68rem',color:'#94a3b8',marginTop:'2px'}}>{format(new Date(p.fecha_pago),'dd/MM/yyyy HH:mm')}</p>
                            </div>
                            <span style={{fontSize:'.92rem',fontWeight:'900',color:'#065f46'}}>+{fmtMonto(p.monto)} Bs.</span>
                        </div>
                    ))}
                </div>
                <div style={{padding:'14px 20px',borderTop:'1.5px solid #f1f5f9',flexShrink:0}}>
                    <button onClick={()=>setSelectedCita(null)} style={{width:'100%',padding:'10px',borderRadius:'11px',border:'1.5px solid #e2e8f0',background:'#f8fafc',color:'#374151',fontWeight:'700',fontSize:'.85rem',cursor:'pointer',transition:'all .2s'}}
                        onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                        onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    if (loading) return (
        <>
            <style>{css}</style>
            <div className="vr-page" style={{background:'#f8fafc',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'14px'}}>
                <div style={{width:'40px',height:'40px',border:`3px solid ${C.secondary}`,borderTopColor:'transparent',borderRadius:'50%',animation:'vrSpin .7s linear infinite'}}/>
                <p role="status" style={{color:'#64748b',fontWeight:'600',fontSize:'.9rem'}}>Cargando reservas...</p>
            </div>
        </>
    );

    return (
        <>
            <style>{css}</style>
            <div className="vr-page" style={{background:'#f8fafc',minHeight:'100vh',padding:'20px 24px'}}>

                {/* ── HEADER ── */}
                <div style={{background:'#fff',borderRadius:'20px',border:'1.5px solid #f1f5f9',boxShadow:'0 4px 20px rgba(0,0,0,.04)',marginBottom:'20px',overflow:'hidden'}}>
                    <div style={{height:'3px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})`}}/>
                    <div style={{padding:'16px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                            <button aria-label="Volver" onClick={()=>navigate(-1)} style={{width:'36px',height:'36px',borderRadius:'10px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',transition:'all .2s',flexShrink:0}}
                                onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background=`${C.secondary}10`;(e.currentTarget as HTMLButtonElement).style.borderColor=`${C.secondary}25`;}}
                                onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='#f8fafc';(e.currentTarget as HTMLButtonElement).style.borderColor='#f1f5f9';}}>
                                <ArrowLeft size={15} color={C.secondary}/>
                            </button>
                            <div style={{width:'36px',height:'36px',borderRadius:'10px',background:`${C.primary}10`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <Calendar size={17} color={C.primary}/>
                            </div>
                            <div>
                                <h1 style={{fontSize:'1.05rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em',lineHeight:1.1}}>
                                    Reservas del <span style={{color:C.primary}}>Espacio</span>
                                </h1>
                                <p style={{fontSize:'.72rem',color:'#94a3b8',marginTop:'2px',fontWeight:'500'}}>Activas y pendientes · Espacio #{espacioId}</p>
                            </div>
                        </div>
                        <div style={{textAlign:'right'}}>
                            <p style={{fontSize:'1.6rem',fontWeight:'900',color:C.secondary,lineHeight:1}}>{citas.length}</p>
                            <p style={{fontSize:'.68rem',color:'#94a3b8',fontWeight:'500'}}>{citas.length===1?'reserva':'reservas'}</p>
                        </div>
                    </div>
                </div>

                {/* ── ERROR (distinto del vacío) ── */}
                {error&&(
                    <div role="alert" style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'14px',padding:'14px 18px',marginBottom:'16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap'}}>
                        <span style={{fontSize:'.85rem',fontWeight:'700',color:'#b91c1c'}}>{error}</span>
                        <button type="button" onClick={()=>setReloadKey(k=>k+1)} style={{display:'inline-flex',alignItems:'center',gap:'6px',minHeight:'36px',padding:'6px 12px',borderRadius:'10px',border:'1px solid #fecaca',background:'#fff',color:'#b91c1c',fontSize:'.8rem',fontWeight:'800',cursor:'pointer'}}>
                            <RefreshCw size={13}/> Reintentar
                        </button>
                    </div>
                )}

                {/* ── CONTENIDO ── */}
                {!error&&!canLoad&&(
                    <div style={{background:'#fff',borderRadius:'20px',border:'1.5px solid #f1f5f9',padding:'40px 24px',textAlign:'center'}}>
                        <p style={{fontSize:'.9rem',fontWeight:'700',color:'#0f172a'}}>Necesitas una sesión POS con sucursal y un espacio para ver sus reservas.</p>
                    </div>
                )}
                {!error&&canLoad&&citas.length===0&&(
                    <div style={{background:'#fff',borderRadius:'20px',border:'1.5px solid #f1f5f9',padding:'60px 24px',textAlign:'center'}}>
                        <div style={{width:'56px',height:'56px',borderRadius:'16px',background:`${C.secondary}10`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px'}}>
                            <Search size={24} color={C.secondary} style={{opacity:.5}}/>
                        </div>
                        <p style={{fontSize:'1rem',fontWeight:'800',color:'#0f172a',marginBottom:'6px'}}>Sin reservas activas</p>
                        <p style={{fontSize:'.85rem',color:'#94a3b8'}}>Este espacio no tiene reservas activas o pendientes.</p>
                        <div style={{display:'flex',gap:'5px',justifyContent:'center',marginTop:'16px'}}>
                            {[C.primary,C.secondary,C.accentDark,C.secondaryLight].map((c,i)=><div key={i} style={{height:'2px',width:i===0?'16px':'6px',borderRadius:'2px',background:c,opacity:.3}}/>)}
                        </div>
                    </div>
                )}
                {!error&&citas.length>0&&(
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:'16px'}}>
                        {citas.map((cita,cardIdx)=>{
                            const tiempoCosto  = calcularTiempoYCosto(cita);
                            const totalPagado  = Number(cita.monto_pagado)||0;
                            const pendiente    = Number(cita.total)-totalPagado;
                            const usaTimer     = citaUsaTimer(cita);
                            const topColor     = cita.estado_pago==='pagado completo'?C.secondaryLight:cita.estado==='Cancelada'?C.primary:C.secondary;
                            const eb  = estadoBadge(cita.estado||'');
                            const epb = estadoPagoBadge(cita.estado_pago);
                            const ecb = ejecucionBadge(cita.estado_ejecucion||'');

                            return (
                                <div key={cita.id} className="vr-card" style={{animationDelay:`${cardIdx*.05}s`}}>
                                    {/* Top accent */}
                                    <div style={{height:'3px',background:topColor,flexShrink:0}}/>

                                    <div style={{padding:'16px 18px 18px',flex:1,display:'flex',flexDirection:'column',gap:'12px'}}>

                                        {/* Header: código + badges + menú */}
                                        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'8px'}}>
                                            <div style={{flex:1,minWidth:0}}>
                                                <p style={{fontSize:'.78rem',fontWeight:'800',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'5px'}}>
                                                    {cita.codigo||`CITA-${cita.id}`}
                                                </p>
                                                <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                                                    <span className="vr-badge" style={ecb}>
                                                        {ejecucionIcon(cita.estado_ejecucion||'')} {ejecucionLabel(cita.estado_ejecucion||'')}
                                                    </span>
                                                    <span className="vr-badge" style={eb}>
                                                        {estadoIcon(cita.estado||'')} {cita.estado}
                                                    </span>
                                                    <span className="vr-badge" style={epb}>
                                                        {cita.estado_pago||'Sin pago'}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Menú dropdown */}
                                            <div style={{position:'relative',flexShrink:0}} data-dd="true">
                                                <button data-dd="true" aria-label={`Acciones de ${cita.codigo||`CITA-${cita.id}`}`} aria-haspopup="menu" aria-expanded={openDropdownId===cita.id} onClick={()=>setOpenDropdownId(openDropdownId===cita.id?null:cita.id)}
                                                    style={{width:'32px',height:'32px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',transition:'all .2s'}}
                                                    onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                                                    onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                                                    <MoreVertical size={14} color="#64748b"/>
                                                </button>
                                                {openDropdownId===cita.id&&(
                                                    <div data-dd="true" className="vr-dropdown" style={{position:'absolute',top:'calc(100% + 6px)',right:0,width:'170px',background:'#fff',borderRadius:'12px',border:'1.5px solid #f1f5f9',boxShadow:'0 8px 28px rgba(0,0,0,.1)',zIndex:50,overflow:'hidden'}}>
                                                        <div style={{height:'3px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})`}}/>
                                                        <div style={{padding:'4px 0'}}>
                                                            <p style={{padding:'6px 12px',fontSize:'.65rem',fontWeight:'800',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.06em'}}>Acciones</p>
                                                            <button onClick={()=>handleVerPagos(cita)} style={{display:'flex',alignItems:'center',gap:'8px',width:'100%',padding:'9px 12px',border:'none',background:'none',cursor:'pointer',fontSize:'.82rem',fontWeight:'600',color:'#374151',transition:'background .15s'}}
                                                                onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background=`${C.secondary}08`}
                                                                onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='none'}>
                                                                <CreditCard size={13} color={C.secondary}/> Ver pagos
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Cliente */}
                                        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                                            <div style={{width:'32px',height:'32px',borderRadius:'10px',background:`${C.secondary}10`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                <User size={14} color={C.secondary}/>
                                            </div>
                                            <div style={{flex:1,minWidth:0}}>
                                                <p style={{fontSize:'.85rem',fontWeight:'700',color:'#0f172a'}}>
                                                    {cita.clienteInfo?.usuario?`${cita.clienteInfo.usuario.first_name} ${cita.clienteInfo.usuario.last_name}`:`Cliente #${cita.cliente}`}
                                                </p>
                                                {cita.clienteInfo?.usuario?.email&&(
                                                    <p style={{fontSize:'.7rem',color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cita.clienteInfo.usuario.email}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Fechas */}
                                        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                                            <div style={{width:'32px',height:'32px',borderRadius:'10px',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                                <Calendar size={14} color="#64748b"/>
                                            </div>
                                            <div>
                                                <p style={{fontSize:'.83rem',fontWeight:'600',color:'#0f172a'}}>
                                                    {fmtDate(cita.fecha_inicio)}
                                                    {cita.fecha_fin&&cita.fecha_fin!==cita.fecha_inicio&&<span style={{color:'#94a3b8'}}> → {fmtDate(cita.fecha_fin)}</span>}
                                                </p>
                                                <p style={{display:'flex',alignItems:'center',gap:'3px',fontSize:'.72rem',color:'#94a3b8'}}>
                                                    <Clock size={10}/> {fmtTime(cita.hora_inicio)}{cita.hora_fin?` - ${fmtTime(cita.hora_fin)}`:' · En curso'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Montos + temporizador */}
                                        <div style={{background:'#f8fafc',borderRadius:'12px',padding:'12px 14px',border:'1.5px solid #f1f5f9'}}>
                                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:tiempoCosto?'10px':0}}>
                                                <div>
                                                    <p style={{fontSize:'.65rem',color:'#94a3b8',fontWeight:'600',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:'3px'}}>
                                                        {tiempoCosto?'Costo actual':'Total'}
                                                    </p>
                                                    <p style={{fontSize:'1.15rem',fontWeight:'900',color:'#0f172a'}}>
                                                        {tiempoCosto?fmtCurrency(tiempoCosto.costo):fmtCurrency(Number(cita.total))}
                                                    </p>
                                                    {cita.descuento_directo&&Number(cita.descuento_directo)>0&&(
                                                        <p style={{fontSize:'.68rem',color:C.primary,fontWeight:'600',marginTop:'2px'}}>Descuento: {fmtCurrency(Number(cita.descuento_directo))}</p>
                                                    )}
                                                </div>
                                                <div style={{textAlign:'right'}}>
                                                    <p style={{fontSize:'.65rem',color:'#94a3b8',fontWeight:'600',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:'3px'}}>Pagado</p>
                                                    <p style={{fontSize:'.92rem',fontWeight:'800',color:totalPagado>=Number(cita.total)?C.secondaryLight:C.accentDark}}>
                                                        {fmtCurrency(totalPagado)}
                                                    </p>
                                                    {pendiente>0&&<p style={{fontSize:'.68rem',color:'#94a3b8',marginTop:'1px'}}>Saldo: {fmtCurrency(pendiente)}</p>}
                                                </div>
                                            </div>

                                            {/* Tiempo temporizador */}
                                            {tiempoCosto&&(
                                                <div style={{paddingTop:'10px',borderTop:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                                    <div style={{display:'flex',alignItems:'center',gap:'5px'}}>
                                                        <Timer size={13} color={tiempoCosto.activa?C.accentDark:'#94a3b8'} style={{animation:tiempoCosto.activa?'vrPulse 1.5s infinite':undefined}}/>
                                                        <span style={{fontSize:'.72rem',color:'#64748b',fontWeight:'500'}}>Tiempo / A cobrar</span>
                                                    </div>
                                                    <div style={{textAlign:'right'}}>
                                                        <p style={{fontSize:'.85rem',fontWeight:'800',color:'#0f172a'}}>
                                                            {Math.floor(tiempoCosto.minutos/60)}h {tiempoCosto.minutos%60}m
                                                        </p>
                                                        <p style={{fontSize:'.68rem',fontWeight:'700',color:C.secondary}}>
                                                            Cobro por {tiempoCosto.horas} {tiempoCosto.horas===1?'hora':'horas'}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {totalPagado > 0 && <FacturaReserva citaId={cita.id}/>}
                                        {/* Acciones */}
                                        <div style={{display:'flex',gap:'8px',paddingTop:'4px',borderTop:'1.5px solid #f8fafc',marginTop:'auto'}}>
                                            {tiempoCosto?.activa&&usaTimer&&!cita.hora_fin&&(
                                                <button className="vr-btn-primary blue" style={{flex:1}} disabled={finalizingId===cita.id||sesionPosId===null||!operadorToken}
                                                    title={sesionPosId===null?'Necesitas una sesión POS abierta para finalizar':(!operadorToken?'Identifica al operador de caja con su PIN para finalizar':undefined)}
                                                    onClick={()=>handleFinalizar(cita)}>
                                                    <Clock size={14}/> {finalizingId===cita.id?'Finalizando…':'Finalizar'}
                                                </button>
                                            )}

                                            {pendiente>0&&(
                                                <button className="vr-btn-primary red" style={{flex:1}} disabled={sesionPosId===null||!operadorToken}
                                                    title={sesionPosId===null?'Necesitas una sesión POS abierta para cobrar':(!operadorToken?'Identifica al operador de caja con su PIN para cobrar':undefined)}
                                                    onClick={()=>{if(sesionPosId===null||!operadorToken)return;setSelectedCitaForPayment(cita);setPagosModalOpen(true);}}>
                                                    <Zap size={14}/> Pagar
                                                </button>
                                            )}

                                            {pendiente<=0&&totalPagado>0&&!(tiempoCosto?.activa&&usaTimer&&!cita.hora_fin)&&(
                                                <div data-testid="vr-paid-badge" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',padding:'9px',borderRadius:'11px',background:`${C.secondaryLight}10`,border:`1.5px solid ${C.secondaryLight}25`,fontSize:'.82rem',fontWeight:'700',color:'#065f46'}}>
                                                    <CheckCircle2 size={14} color={C.secondaryLight}/> Pagado
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal pago: cobra SIEMPRE con la caja explícita de esta identidad */}
            {selectedCitaForPayment&&sesionPosId!==null&&(
                <PagosModal
                    isOpen={isPagosModalOpen}
                    onClose={()=>{setPagosModalOpen(false);setSelectedCitaForPayment(null);}}
                    citaId={selectedCitaForPayment.id}
                    montoCita={Number(selectedCitaForPayment.total)}
                    montoPagado={Number(selectedCitaForPayment.monto_pagado)||0}
                    sesionPosId={sesionPosId}
                    operadorToken={operadorToken}
                    onPagoSuccess={handlePagoSuccess}
                />
            )}

            {/* Modal historial pagos */}
            {ModalHistorial}
        </>
    );
};

export default VerReservas;
